import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Bell, X, Check } from 'lucide-react';
import { requestNotificationPermission, onMessageListener } from '../firebase/config';
import { notificationsAPI } from '../api/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const NotificationPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState('default');
  const [isIOS, setIsIOS] = useState(false);
  const { user } = useAuth(); // Get user state

  useEffect(() => {
    // Check if running inside native WebToNative app - notifications handled by WTN
    if (window.WebToNativeInterface || window.webkit?.messageHandlers?.webtonative) {
      console.log('[Notification] Running inside native app - prompt handled by WebToNative');
      return;
    }

    // Skip during automated testing / Lighthouse / bots
    if (navigator.webdriver || document.hidden) return;

    // Detect iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);

    // Check notification permission status
    if ('Notification' in window) {
      setPermissionStatus(Notification.permission);
      
      // If already granted and enabled, don't show prompt
      if (Notification.permission === 'granted' && localStorage.getItem('notification-enabled') === 'true') {
        console.log('[Notification] Already enabled, not showing prompt');
        // Still listen for foreground messages
        onMessageListener((payload) => {
          console.log('[Notification] Foreground message:', payload);
          if (payload.notification) {
            toast.success(payload.notification.title, {
              description: payload.notification.body,
              duration: 5000
            });
          }
        });
        return;
      }
      
      // If permission is already denied, DON'T show the prompt at all
      // (it will just trigger Chrome's "blocked" native popup which is confusing)
      // The user can re-enable from Settings or the Bell dropdown
      if (Notification.permission === 'denied') {
        console.log('[Notification] Permission denied — not showing prompt to avoid confusion');
        return;
      }
      
      // Show prompt after meaningful user interaction (not immediately on load)
      if (Notification.permission === 'default') {
        // Never show more than once per session
        if (sessionStorage.getItem('notif-prompt-shown-session')) return;

        const dismissed     = localStorage.getItem('notification-prompt-dismissed');
        const dismissedTime = localStorage.getItem('notification-prompt-dismissed-time');

        if (dismissed && dismissedTime) {
          const daysSince = (Date.now() - parseInt(dismissedTime)) / (1000 * 60 * 60 * 24);
          if (daysSince < 15) return;
          localStorage.removeItem('notification-prompt-dismissed');
          localStorage.removeItem('notification-prompt-dismissed-time');
        } else if (dismissed && !dismissedTime) {
          localStorage.setItem('notification-prompt-dismissed-time', Date.now().toString());
          return;
        }

        // ✅ تجاوزنا كل الفحوصات — احفظ الـ flag فوراً (15 يوم من الآن)
        // قبل أي تفاعل أو تنقّل بين الصفحات
        localStorage.setItem('notification-prompt-dismissed', 'true');
        localStorage.setItem('notification-prompt-dismissed-time', Date.now().toString());
        sessionStorage.setItem('notif-prompt-shown-session', 'true');

        // أظهر النافذة بعد أول تفاعل + 8 ثوانٍ
        let shown = false;
        let interactionTimer = null;

        const triggerShow = () => {
          if (shown) return;
          shown = true;
          document.removeEventListener('click', triggerShow);
          document.removeEventListener('touchstart', triggerShow);
          interactionTimer = setTimeout(() => {
            console.log('[Notification] Showing prompt');
            setShowPrompt(true);
          }, 8000);
        };

        document.addEventListener('click', triggerShow, { once: true, passive: true });
        document.addEventListener('touchstart', triggerShow, { once: true, passive: true });

        return () => {
          clearTimeout(interactionTimer);
          document.removeEventListener('click', triggerShow);
          document.removeEventListener('touchstart', triggerShow);
        };
      }
    }

    // Listen for foreground messages
    onMessageListener((payload) => {
      console.log('[Notification] Foreground message:', payload);
      if (payload.notification) {
        toast.success(payload.notification.title, {
          description: payload.notification.body,
          duration: 5000
        });
      }
    });
  }, []); // Run once on mount - no dependency on user

  const handleEnableNotifications = async () => {
    try {
      console.log('[Notification] Requesting permission...');
      
      setShowPrompt(false);

      // iOS Safari (non-PWA) doesn't support push notifications
      if (isIOS && !window.navigator.standalone && !window.matchMedia('(display-mode: standalone)').matches) {
        toast.info('لتفعيل الإشعارات على الآيفون', {
          description: 'افتح القائمة ثم اضغط "أضف إلى الشاشة الرئيسية" ثم افتح التطبيق منها',
          duration: 8000,
        });
        handleDismiss();
        return;
      }
      
      const loadingToast = toast.loading('جاري تفعيل الإشعارات...', {
        description: 'الرجاء الانتظار قليلاً'
      });
      
      const token = await requestNotificationPermission();
      
      console.log('[Notification] Token received:', token ? 'YES (length=' + token.length + ')' : 'NO (check console for error)');
      
      if (token) {
        // Save token locally first
        localStorage.setItem('fcm-token', token);
        localStorage.setItem('notification-enabled', 'true');
        setPermissionStatus('granted');

        // Save token to backend for ALL users (logged-in or anonymous)
        try {
          console.log('[Notification] Saving token to backend...');
          await notificationsAPI.saveToken(token);
          console.log('[Notification] ✅ Token saved to backend successfully');
          toast.dismiss(loadingToast);
          toast.success('تم تفعيل الإشعارات بنجاح! ✅', {
            description: 'ستصلك إشعارات الأهداف والمباريات مباشرة',
            duration: 5000
          });
        } catch (error) {
          console.error('[Notification] Error saving token to backend:', error);
          toast.dismiss(loadingToast);
          // Still show success — token is cached locally and will retry on next load
          toast.success('تم تفعيل الإشعارات! ✅', {
            description: 'ستصلك إشعارات المباريات',
            duration: 5000
          });
        }
      } else if ('Notification' in window && Notification.permission === 'granted') {
        // Permission granted but token not ready — schedule background retry
        console.warn('[Notification] Permission granted but FCM token failed — scheduling retry in 5s');
        localStorage.setItem('notification-enabled', 'true');
        localStorage.setItem('fcm-token-pending', 'true');
        setPermissionStatus('granted');
        toast.dismiss(loadingToast);
        toast.success('تم منح الإذن! ✅', {
          description: 'جاري الاتصال بخدمة الإشعارات...',
          duration: 5000
        });

        // Retry after short delay (browser might need time to set up push subscription)
        setTimeout(async () => {
          try {
            const { getFCMToken } = await import('../firebase/config');
            const retryToken = await getFCMToken();
            if (retryToken) {
              localStorage.setItem('fcm-token', retryToken);
              localStorage.removeItem('fcm-token-pending');
              await notificationsAPI.saveToken(retryToken);
              console.log('[Notification] ✅ Retry successful — token saved');
            } else {
              console.error('[Notification] ❌ Retry also failed — check Firebase/VAPID setup');
            }
          } catch (e) {
            console.error('[Notification] Retry error:', e);
          }
        }, 5000);
      } else {
        console.log('[Notification] No token received and permission not granted');
        toast.dismiss(loadingToast);
        toast.error('لم يتم منح إذن الإشعارات', {
          description: 'يمكنك تفعيلها لاحقاً من إعدادات المتصفح'
        });
        handleDismiss();
      }
    } catch (error) {
      console.error('[Notification] Error enabling notifications:', error);
      toast.error('حدث خطأ أثناء تفعيل الإشعارات', {
        description: error.message
      });
      handleDismiss();
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    sessionStorage.setItem('notif-prompt-shown-session', 'true');
    localStorage.setItem('notification-prompt-dismissed', 'true');
    localStorage.setItem('notification-prompt-dismissed-time', Date.now().toString());
  };

  // Don't show if notifications not supported or prompt not active
  if (!('Notification' in window) || !showPrompt) {
    return null;
  }

  return (
    <div
      className="fixed bottom-20 left-3 right-3 z-50"
      style={{ fontFamily: 'Tajawal, sans-serif', animation: 'slideUp 0.3s ease-out' }}
    >
      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div className="relative flex items-center gap-3 px-3 py-2.5 rounded-2xl overflow-hidden"
        style={{
          background: 'rgba(10,15,26,0.97)',
          border: '1px solid rgba(234,179,8,0.2)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.5), 0 0 0 1px rgba(234,179,8,0.05)',
          backdropFilter: 'blur(16px)',
        }}
      >
        {/* top line accent */}
        <div className="absolute top-0 left-0 right-0 h-[1.5px]"
          style={{ background: 'linear-gradient(90deg, transparent, rgba(234,179,8,0.6), transparent)' }} />

        {/* Bell icon */}
        <div className="relative shrink-0 w-9 h-9 rounded-xl flex items-center justify-center"
          style={{ background: 'rgba(234,179,8,0.1)', border: '1px solid rgba(234,179,8,0.2)' }}>
          <Bell className="w-4 h-4 text-yellow-400" />
          <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-red-500 ring-2 animate-pulse"
            style={{ ringColor: '#0a0f1a' }} />
        </div>

        {/* Text */}
        <div className="flex-1 min-w-0">
          <p className="text-white font-black text-[12px] leading-tight">لا تفوّت أي هدف</p>
          {isIOS
            ? <p className="text-gray-400 text-[10px] mt-0.5 leading-tight">أضفه للشاشة الرئيسية أولاً</p>
            : <p className="text-gray-400 text-[10px] mt-0.5 leading-tight">أهداف · بطاقات · نتائج مباشرة</p>
          }
        </div>

        {/* CTA */}
        <button
          onClick={handleEnableNotifications}
          data-testid="notif-prompt-enable"
          className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-xl font-black text-[11px] transition-all active:scale-95"
          style={{ background: '#eab308', color: '#000', boxShadow: '0 2px 8px rgba(234,179,8,0.25)' }}
        >
          <Bell className="w-3 h-3" />
          فعّل
        </button>

        {/* Dismiss */}
        <button
          onClick={handleDismiss}
          data-testid="notif-prompt-close"
          className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-all"
          style={{ background: 'rgba(255,255,255,0.05)', color: '#9ca3af' }}
          aria-label="إغلاق"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

export default NotificationPrompt;
